import {useState} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Tag} from '@/components/ui/tag'
import {HelpIcon} from '@/components/help-icon'
import {MicroLabel} from '@/components/period-selector'
import {CYCLE_ORDER} from '@/components/sgsst/group-by-toggle'
import {useConstructoresCatalogo, type ConstructorCatalogo} from '@/hooks/sgsst/constructores/use-constructores'
import {type SgsstItem} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {cn} from '@/lib/utils'

// Solo índice (constructores-estandar-spec §3): la acción vive en la fila del checklist; aquí solo
// se enlaza a `#std-{code}`.

const NIVEL_TONE: Record<ConstructorCatalogo['nivel'], 'lime' | 'green' | 'neutral'> = {
  respaldo_hechos: 'lime',
  constructor_artefacto: 'green',
  registro_soporte: 'neutral',
}

function ArtefactoRow({
  entry,
  item,
  businessId,
}: {
  entry: ConstructorCatalogo
  item: SgsstItem | undefined
  businessId: string
}) {
  const {t} = useTranslation()
  const confirmado = item?.status === 'cumple'
  const sugerido = !!item?.sugerido
  // Sin fila = fuera del capítulo vigente: el art. 27 ya lo da por acreditado.
  if (!item) {
    return (
      <li className="flex flex-wrap items-start justify-between gap-3 border-b border-border py-3 last:border-b-0">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs font-medium text-muted-foreground">{entry.standard_code}</span>
            <Tag tone={NIVEL_TONE[entry.nivel]}>{t(`artefactos.nivel.${entry.nivel}`)}</Tag>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{entry.titulo}</p>
        </div>
        <Tag tone="neutral" data-testid={`artefactos-no-aplica-${entry.standard_code}`}>
          {t('artefactos.estado.noAplica')}
        </Tag>
      </li>
    )
  }
  return (
    <li className="flex flex-wrap items-start justify-between gap-3 border-b border-border py-3 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs font-medium text-muted-foreground">{entry.standard_code}</span>
          <Tag tone={NIVEL_TONE[entry.nivel]}>{t(`artefactos.nivel.${entry.nivel}`)}</Tag>
        </div>
        <p className="mt-1 text-sm text-foreground">{entry.titulo}</p>
        {item && <p className="mt-0.5 text-xs text-muted-foreground">{item.name}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {confirmado ? (
          <Tag tone="green">{t('artefactos.estado.confirmado')}</Tag>
        ) : sugerido ? (
          <Tag tone="enCurso" data-testid={`artefactos-sugerido-${entry.standard_code}`}>
            {t('artefactos.estado.sugerido')}
          </Tag>
        ) : (
          <Tag tone="neutral">{t('artefactos.estado.pendiente')}</Tag>
        )}
        {/* `Link` y no `<a href>`: un `<a>` al fragmento del mismo documento no avisa a react-router,
            y `sgsst.tsx` necesita ver el hash para volver al checklist. */}
        <Link
          to={`/businesses/${businessId}/sgsst#std-${entry.standard_code}`}
          className="text-xs font-medium whitespace-nowrap text-primary hover:underline"
          data-testid={`artefactos-link-${entry.standard_code}`}>
          {t('artefactos.irALaFila')}
        </Link>
      </div>
    </li>
  )
}

export function ArtefactosPanel({
  businessId,
  allItems,
  enabled,
}: {
  businessId: string
  allItems: SgsstItem[]
  enabled: boolean
}) {
  const {t} = useTranslation()
  const {data: catalogo, isLoading} = useConstructoresCatalogo(businessId, enabled)
  // R6: los de fuera del capítulo son un chip que cambia de lista, no un «ver todos».
  const [soloFuera, setSoloFuera] = useState(false)
  const itemByCode = new Map(allItems.map((item) => [item.code, item]))

  if (isLoading) return null

  const entries = catalogo ?? []
  const bySection = new Map<string, ConstructorCatalogo[]>()
  const sinFila: ConstructorCatalogo[] = []
  for (const entry of entries) {
    const item = itemByCode.get(entry.standard_code)
    if (!item) {
      sinFila.push(entry)
      continue
    }
    const key = item.cycle
    if (!bySection.has(key)) bySection.set(key, [])
    bySection.get(key)!.push(entry)
  }

  if (entries.length === 0) {
    return (
      <div className="surface px-6 py-10 text-center" id="artefactos" data-testid="artefactos-empty">
        <p className="text-sm text-foreground">{t('artefactos.empty.title')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4" id="artefactos" data-testid="artefactos-panel">
      {sinFila.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            aria-pressed={soloFuera}
            onClick={() => setSoloFuera((puesto) => !puesto)}
            className={cn(
              'rounded-full border px-3 py-1 font-mono text-[11px] tracking-wide',
              soloFuera
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-input text-muted-foreground hover:text-foreground'
            )}
            data-testid="artefactos-ver-todos">
            {t('artefactos.chipFueraDeCapitulo', {count: sinFila.length})}
          </button>
          {soloFuera && (
            <HelpIcon
              label={t('common.help.iconLabelFor', {campo: t('artefactos.sinFilaEsteAno')})}
              testId="artefactos-fuera-de-capitulo-help">
              {t('artefactos.fueraDeCapituloAviso')}
            </HelpIcon>
          )}
        </div>
      )}
      {/* `xl` y no `lg`: medido, en 1024 la columna queda en ~344 px y la fila se parte.
          `minmax(0,…)` + `min-w-0` impiden que un título largo estire la columna. */}
      {!soloFuera && (
        <div className="grid gap-4 xl:grid-cols-[repeat(2,minmax(0,1fr))] xl:items-start">
          {CYCLE_ORDER.filter((cycle) => bySection.has(cycle)).map((cycle) => (
            <section key={cycle} className="surface min-w-0 overflow-hidden">
              <div className="surface-divider border-b px-4 py-2.5">
                <MicroLabel>{cycle}</MicroLabel>
              </div>
              <ul className="divide-y divide-border/60 px-4">
                {bySection.get(cycle)!.map((entry) => (
                  <ArtefactoRow
                    key={entry.standard_code}
                    entry={entry}
                    item={itemByCode.get(entry.standard_code)}
                    businessId={businessId}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
      {soloFuera && (
        <div className="surface overflow-hidden" data-testid="artefactos-fuera-de-capitulo">
          {/* U4: el aviso del art. 27 va una vez, a un clic, nunca fila a fila. */}
          <div className="surface-divider border-b px-4 py-2.5">
            <MicroLabel>{t('artefactos.sinFilaEsteAno')}</MicroLabel>
          </div>
          <ul className="divide-y divide-border/60 px-4">
            {sinFila.map((entry) => (
              <ArtefactoRow key={entry.standard_code} entry={entry} item={undefined} businessId={businessId} />
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
